import AdminHeader from "components/Headers/AdminHeader";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Input,
  Tooltip,
} from "reactstrap";
import {
  renderStars,
  toCamelCase,
} from "components/CommonComponents/CommonFunctions";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { CONTENT_DELIVERY_MODE_MAP } from "components/constant";
import { useDispatch, useSelector } from "react-redux";
import { getChannelPartnerCourseList } from "action/course";
import { courseList } from "constants/course";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";


const ChannelPartnerCourses = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isFirstSearchRender = useRef(true);
  const [courseListState, setCourseListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const rolePath =
    role === "SUPER_ADMIN"
      ? "super-admin"
      : role === "ADMIN"
        ? "admin"
        : role === "CHANNEL_PARTNER"
          ? "channel-partner"
          : "instructor";

  const { pathname } = useLocation();
  const isMyCourse = pathname.includes("/my-courses");
  const mode = isMyCourse ? "MY_COURSE" : "COURSE";
  const [pageNum, setPageNum] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filterState, setFilterState] = useState({
    searchName: "",
    price: "All",
    level: ["All"],
    courseType: ["All"],
    contentDeliveryMode: ["All"],
    rating: "",
    filterParams: {
      filters: [],
      searchString: "",
    },
  });
  const [tooltipOpen, setTooltipOpen] = useState({});
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const courseListData = useSelector((state) => state.course.courseList);
  const statistics = courseListData?.data?.CourseStatistic || {};

  const PRICE_FILTERS = [
    { label: "All", count: statistics?.PriceStatisticInfo?.all || 0 },
    { label: "Paid", count: statistics?.PriceStatisticInfo?.paid || 0 },
    { label: "Free", count: statistics?.PriceStatisticInfo?.free || 0 },
  ];

  const LEVEL_FILTERS = [
    { label: "All", count: statistics?.LevelStatisticInfo?.all || 0 },
    { label: "Beginner", count: statistics?.LevelStatisticInfo?.beginners || 0 },
    { label: "Intermediate", count: statistics?.LevelStatisticInfo?.intermediate || 0 },
    { label: "Advanced", count: statistics?.LevelStatisticInfo?.advanced || 0 },
  ];

  const REVIEW_FILTERS = [
    { stars: 5, count: statistics?.ReviewStatisticInfo?.five || 0 },
    { stars: 4, count: statistics?.ReviewStatisticInfo?.four || 0 },
    { stars: 3, count: statistics?.ReviewStatisticInfo?.three || 0 },
    { stars: 2, count: statistics?.ReviewStatisticInfo?.two || 0 },
    { stars: 1, count: statistics?.ReviewStatisticInfo?.one || 0 },
  ];

  const COURSE_TYPE_FILTERS = [
    { label: "All", count: statistics?.CourseTypeStatisticInfo?.all || 0 },
    { label: "Recorded", count: statistics?.CourseTypeStatisticInfo?.recorded || 0 },
    {
      label: "Live",
      count: statistics?.CourseTypeStatisticInfo?.live || 0,
      subTypes: [
        { label: "Offline", count: statistics?.ContentDeliveryModeStatisticInfo?.offline || 0 },
        { label: "Online", count: statistics?.ContentDeliveryModeStatisticInfo?.online || 0 },
        { label: "Hybrid", count: statistics?.ContentDeliveryModeStatisticInfo?.hybrid || 0 },
      ],
    },
  ];

  useEffect(() => {
    setCourseListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    getChannelPartnerCourseList(
      dispatch,
      pageNum,
      filterState.filterParams,
      debouncedSearch,
      {
        sortBy: "CreatedAt",
        sortType: "desc",
      },
    );

    return () => {
      dispatch({ type: courseList, payload: {} });
    };
  }, [pageNum, filterState.filterParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterState.searchName);
    }, 500);

    return () => clearTimeout(timer);
  }, [filterState.searchName]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    // if (!filterState.searchName) {
    //   return;
    // }

    setPageNum(1);

    setFilterState((prev) => {
      const updatedState = {
        ...prev,
        searchName: debouncedSearch,
      };

      return {
        ...updatedState,
        filterParams: buildFilterParams(updatedState),
      };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    if (courseListData?.code === 200) {
      setCourseListState({
        isLoading: false,
        error: "",
        noRecords: courseListData?.data?.Course?.length === 0,
      });
    } else if (courseListData?.code || courseListData?.message) {
      setCourseListState({
        isLoading: false,
        error:
          courseListData?.message ||
          "Failed to load course list! Please try again.",
        noRecords: false,
      });
    }
  }, [courseListData]);

  const courses = courseListData?.data?.Course || [];
  const totalRecords = courseListData?.data?.totalRecords || 0;
  const totalFilteredRecords = courseListData?.data?.totalFilteredRecords || 0;
  const totalPages = Math.ceil(totalFilteredRecords / 9);

  const buildFilterParams = (state = filterState) => {
    const filters = [];

    if (state.price && state.price !== "All") {
      filters.push({
        field: "PriceType",
        condition: "eq",
        filterValues: [state.price.toUpperCase()],
      });
    }

    if (state.level.length) {
      const levels = state.level.filter((v) => v !== "All");

      if (levels.length) {
        filters.push({
          field: "Level",
          condition: "in",
          filterValues: levels.map((v) => v.toUpperCase()),
        });
      }
    }

    if (state.courseType.length) {
      const courseTypes = state.courseType.filter((v) => v !== "All");

      if (courseTypes.length) {
        filters.push({
          field: "CourseType",
          condition: "in",
          filterValues: courseTypes.map((v) => v.toUpperCase()),
        });
      }
    }

    if (state.rating) {
      filters.push({
        field: "Reviews",
        condition: "gte",
        filterValues: [String(state.rating)],
      });
    }

    if (state.contentDeliveryMode.length) {
      const modes = state.contentDeliveryMode.filter((v) => v !== "All");

      if (modes.length) {
        filters.push({
          field: "ContentDeliveryMode",
          condition: "in",
          filterValues: modes.map((v) => CONTENT_DELIVERY_MODE_MAP[v]),
        });
      }
    }

    return {
      filters: filters.length ? JSON.stringify(filters) : "",
    };
  };

  const handleSingleSelectFilter = (key, value) => {
    setCourseListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: false,
    }));
    setPageNum(1);

    setFilterState((prev) => {
      const updatedState = {
        ...prev,
        [key]: prev[key] === value ? "" : value,
      };

      return {
        ...updatedState,
        filterParams: buildFilterParams(updatedState),
      };
    });
  };

  const handleMultiSelectFilter = (key, value) => {
    setCourseListState((prev) => ({
      ...prev,
      isLoading: true,
      error: "",
      noRecords: false,
    }));
    setPageNum(1);

    setFilterState((prev) => {
      let selected = [...prev[key]];

      if (value === "All") {
        selected = selected.includes("All") ? [] : ["All"];
      } else {
        selected = selected.filter((v) => v !== "All");

        if (selected.includes(value)) {
          selected = selected.filter((v) => v !== value);
        } else {
          selected.push(value);
        }
      }

      const updatedState = {
        ...prev,
        [key]: selected,
      };

      if (key === "courseType" && !selected.includes("Live")) {
        updatedState.contentDeliveryMode = ["All"];
      }

      return {
        ...updatedState,
        filterParams: buildFilterParams(updatedState),
      };
    });
  };

  const toggleTooltip = (key) => {
    setTooltipOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;

    setFilterState((prev) => ({
      ...prev,
      searchName: value,
    }));
  };

  //   const handleNavigation = (courseId) => {
  //     navigate(`/user/course/${urlEncodedId(courseId)}/details`);
  //   };
  const handleNavigation = (courseId) => {
    const targetPath = isMyCourse
      ? `/${rolePath}/my-courses/${urlEncodedId(courseId)}/details`
      : `/${rolePath}/course/${urlEncodedId(courseId)}/details`;

    navigate(targetPath, { state: { mode: mode } });
  };




  const hasActiveFilters =
    filterState.price !== "All" ||
    filterState.level.some((v) => v !== "All") ||
    filterState.courseType.some((v) => v !== "All") ||
    filterState.contentDeliveryMode.some((v) => v !== "All") ||
    filterState.rating !== "" ||
    filterState.searchName !== "";

  const handleClearFilters = () => {
    setFilterState({
      searchName: "",
      price: "All",
      level: ["All"],
      courseType: ["All"],
      contentDeliveryMode: ["All"],
      rating: "",
      filterParams: {
        filters: [],
        searchString: "",
      },
    });
    setDebouncedSearch("");
    if (pageNum !== 1) setPageNum(1);
  };

  const isLiveChecked = filterState.courseType.includes("Live");

  const getCourseTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case "offline":
        return "Offline";
      case "online":
      case "live":
        return "Online";
      case "recorded":
        return "Recorded";
      case "both":
        return "Hybrid";
      default:
        return "Course";
    }
  };

  const getCourseTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "offline":
        return "ni ni-building";
      case "online":
      case "live":
        return "ni ni-world";
      case "recorded":
        return "ni ni-button-play";
      case "both":
       return "ni ni-app";
      default:
        return "ni ni-book-bookmark";
    }
  };

  return (
    <>
      <AdminHeader name="All Courses" />

      <Container fluid className="mt--6 mb-4">
        <div className="d-flex align-items-center mb-4">
          <div className="d-flex align-items-center justify-content-end justify-content-md-start ml-auto flex-nowrap">
            <InputGroup className="mr-4 w-80 search-bottom-border">
              <Input
                type="text"
                placeholder="Search title, category"
                className="text-white-input"
                value={filterState.searchName}
                onChange={handleSearchChange}
              />
              <InputGroupAddon addonType="append">
                <InputGroupText>
                  <i className="fa fa-search" />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>

        <div className="d-lg-none d-flex justify-content-end mt-5 mb-1 w-100">
          <Button
            color="primary"
            className="btn-deep-blue"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        <Row className="mt-5">
          {/* left card grid for courses */}
          <Col xl="9" lg="8" md="12">
            {courseListState.isLoading ? (
              <div className="loaderContainer no-courses-container">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : courseListState.error ? (
              <div className="loaderContainer error-style no-courses-container">
                <div className="error-icon">
                  <i className="fa-solid fa-circle-exclamation"></i>
                </div>
                <span>{courseListState.error}</span>
              </div>
            ) : courseListState.noRecords ? (
              <div className="loaderContainer empty-style no-courses-container">
                <div className="empty-icon">
                  <i className="fa-solid fa-circle-info"></i>
                </div>
                <span>No courses found.</span>
              </div>
            ) : (
              <>
                <div className="courses-grid">
                  {courses.map((course) => (
                    <div key={course.id} className="course-card-wrapper">
                      <Card
                        className="course-card mb-1 cursor"
                        onClick={() => handleNavigation(course.id)}
                      >
                        {/* Course Image */}
                        <div className="course-image-wrapper">
                          {course.media_url?.match(/\.(mp4|webm|ogg|mov|m4v)$/i) ? (
                            <video
                              className="course-image"
                              src={course.media_url}
                              muted
                              preload="metadata"
                              playsInline
                              disablePictureInPicture
                              controls={false}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <img
                              src={course.media_url}
                              alt={course.course_name}
                              className="course-image"
                            />
                          )}

                        {course.course_type && (
                          <span
                            className={`radio-badge image-radio-badge ${
                              course.course_type === "LIVE" ? "live" : "recorded"
                            }`}
                          >
                            <span className="radio-circle">
                              <span className="radio-dot" />
                            </span>
                            {course.course_type}
                          </span>
                        )}
                        {course?.content_delivery_mode !== "" && <span className="course-overview__badges">
                            <span
                                  className={`course-type-pill ${
                                    course?.content_delivery_mode?.toLowerCase() ===
                                    "offline"
                                      ? "offline"
                                      : course?.content_delivery_mode?.toLowerCase() ===
                                          "recorded"
                                        ? "recorded"
                                        : course?.content_delivery_mode?.toLowerCase() ===
                                            "both"
                                          ? "hybrid"
                                          : "live"
                                  }`}
                                >
                                  <i
                                    className={getCourseTypeIcon(
                                      course?.content_delivery_mode,
                                    )}
                                  />
                                  {getCourseTypeLabel(
                                    course?.content_delivery_mode,
                                  )}
                                </span>
                          </span>}
                        </div>

                        {/* Course Details */}
                        <CardBody className="pt-3 pb-1 ml--1 mr--1">
                          {/* Course Title */}
                          <div className="small-text text-deep-blue font-weight-bold">
                            <div
                              id={`courseTitleTooltip-${course.id}`}
                              className="course-title-ellipsis"
                            >
                              {course.course_name}
                            </div>
                            <Tooltip
                              className="qb-tooltip"
                              placement="auto"
                              delay={{ show: 150, hide: 0 }}
                              target={`courseTitleTooltip-${course.id}`}
                              isOpen={tooltipOpen[`title-${course.id}`]}
                              toggle={() => toggleTooltip(`title-${course.id}`)}
                              autohide={false}
                            >
                              {course.course_name}
                            </Tooltip>
                          </div>

                          {/* Level and Category */}
                          <div className="d-flex justify-content-between mt-3">
                            <div>
                              <div className="text-muted small">Level</div>
                              <div className="xs-text font-weight-bold">{toCamelCase(course.level)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-muted small">Category</div>
                              <div>
                                <span
                                  id={`courseCategoryTooltip-${course.id}`}
                                  className="course-category-ellipsis xs-text font-weight-bold"
                                >
                                  {course.category}
                                </span>
                              </div>
                              <Tooltip
                                className="qb-tooltip"
                                placement="auto"
                                delay={{ show: 150, hide: 0 }}
                                isOpen={tooltipOpen[`category-${course.id}`]}
                                target={`courseCategoryTooltip-${course.id}`}
                                toggle={() => toggleTooltip(`category-${course.id}`)}
                                autohide={false}
                              >
                                {course.category}
                              </Tooltip>
                            </div>
                          </div>
                          {/* Created by and Created at */}
                          <div className="d-flex justify-content-between mt-3">
                            <div>
                              <div className="text-muted small">Created by</div>
                              <div className="xs-text font-weight-bold">{course.created_by_name}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-muted small">Created at</div>
                              <div className="xs-text font-weight-bold">{course.created_at}</div>
                            </div>
                          </div>
                          {/* Rating and Enrolled trainees */}
                          <div className="d-flex justify-content-between mt-3">
                            <div>
                              <div className="text-muted small">Rating</div>
                              <div className="rating-wrapper">
                                {renderStars(course.average_rating)}
                              </div>
                            </div>
                          </div>



                          {/* Price and Enroll - same line */}
                          <div className="d-flex mt-3 mb-2 justify-content-between align-items-center" style={{
                            borderTop: "1px solid #e9ecef",
                            paddingTop: "12px",
                          }}>
                            <div className="course-price">
                              <div className="text-muted small">Price</div>
                              <div className="small-text text-default font-weight-bold">
                                {Number(course.price) > 0
                                  ? `₹${Number(course.price).toFixed(2)}`
                                  : "Free"}
                              </div>
                            </div>

                            <Button
                              color="link"
                              className="text-deep-blue p-0 mt-4"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigation(course.id);
                              }}
                            >
                              Enroll
                              <i className="fas fa-arrow-right ml-1" />
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  ))}
                </div>

                <CustomPagination
                  totalPages={totalPages}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                />
              </>
            )}
          </Col>

          {/* Right: Filters */}
          <Col
            xl="3"
            lg="4"
            md="12"
            className={`filters-wrapper ${showFilters ? "show-filters" : ""}`}
          >
            <Card className="filters-card mb-4">
              <CardBody>
                <div className="filters-header">
                  <div>
                    <h5 className="text-muted mb-2">Filters</h5>
                    {hasActiveFilters ? (
                      <div className="filter-summary">
                        Showing <strong>{totalFilteredRecords}</strong> of{" "}
                        <strong>{totalRecords}</strong> Courses
                      </div>
                    ) : (
                      <div className="filter-summary">
                        <strong>{totalRecords}</strong> Courses
                      </div>
                    )}
                  </div>

                  {hasActiveFilters && (
                    <Button
                      color="link"
                      className="clear-btn p-0"
                      onClick={handleClearFilters}
                    >
                      <i className="fa fa-times mr-1 mt-1" />
                      Clear
                    </Button>
                  )}

                  <i
                    className="fa-solid fa-x d-lg-none cursor"
                    onClick={() => setShowFilters(!showFilters)}
                  />
                </div>

                {/* Price */}
                <div className="mb-4">
                  <h5 className="text-muted mb-2">Price</h5>
                  {PRICE_FILTERS.map((item) => (
                    <div
                      key={item.label}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0">
                        <input
                          type="checkbox"
                          name="price"
                          className="mr-2"
                          checked={filterState.price === item.label}
                          onChange={() => handleSingleSelectFilter("price", item.label)}
                        />
                        {item.label}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Levels */}
                <div className="mb-4">
                  <h5 className="text-muted mb-2">Levels</h5>
                  {LEVEL_FILTERS.map((item) => (
                    <div
                      key={item.label}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={filterState.level.includes(item.label)}
                          onChange={() => handleMultiSelectFilter("level", item.label)}
                        />
                        {item.label}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Reviews */}
                <div className="mb-4">
                  <h5 className="text-muted mb-2">Reviews</h5>
                  {REVIEW_FILTERS.map((item) => (
                    <div
                      key={item.stars}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0 d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={filterState.rating === String(item.stars)}
                          onChange={() =>
                            handleSingleSelectFilter("rating", String(item.stars))
                          }
                        />
                        {renderStars(item.stars, false)}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Course Type */}
                <div>
                  <h5 className="text-muted mb-2">Course Type</h5>

                  {COURSE_TYPE_FILTERS.map((item) => (
                    <div key={item.label} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <label className="mb-0 cursor">
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={filterState.courseType.includes(item.label)}
                            onChange={() => handleMultiSelectFilter("courseType", item.label)}
                          />
                          {item.label}
                        </label>
                        <span className="text-muted small">{item.count}</span>
                      </div>

                      {item.label === "Live" && isLiveChecked && item.subTypes && (
                        <div className="mt-3">
                          <h5 className="text-muted mb-2">Content Delivery Mode</h5>
                          {item.subTypes.map((sub) => (
                            <div
                              key={sub.label}
                              className="d-flex justify-content-between align-items-center mb-1"
                            >
                              <label className="mb-0 cursor">
                                <input
                                  type="checkbox"
                                  className="mr-2"
                                  checked={filterState.contentDeliveryMode.includes(sub.label)}
                                  onChange={() =>
                                    handleMultiSelectFilter("contentDeliveryMode", sub.label)
                                  }
                                />
                                {sub.label}
                              </label>
                              <span className="text-muted small">{sub.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ChannelPartnerCourses;